<template>
  <div class="fms-dashboard">
    <data-filter :onApplyFilter="onApplyFilter" :hideDate="true" :hideRangeType="true" :hideInterval="true" />

    <div class="dashboard-card">
      <template v-if="isLoading">
        <div class="text-center mt-5">
          <b-spinner label="Spinning"></b-spinner>
        </div>
      </template>

      <template v-else-if="currAssetSummary && !isLoading">
        <!-- Data percentage -->
        <div style="padding: 0 10px;">
          <!-- <data-percentage /> -->
        </div>
        <!-- ./ Data percentage -->

        <section class="fms-header">
          <div>
            <h1>{{ currAsset.massName }} - Dashboard FMS</h1>
            <div class="overview-tags">
              <span class="tag">{{ currAsset?.massMaty?.matyName ?? '-' }}</span>
              <span class="tag tag-soft">24H Overview</span>
              <span class="tag tag-outline">{{ rangeLabel }}</span>
            </div>
          </div>
          <div class="header-status">
            <span class="status-pill" :class="{ 'status-pill--alert': !isRealtimeConnected }">
              <span class="status-dot" :class="{ 'status-dot--alert': !isRealtimeConnected }"></span>
              {{ isRealtimeConnected ? 'Realtime Connected' : 'Disconnected' }}
            </span>
            <span class="status-date">Last Data: {{ currAssetLastData?.gps?.timestamp }}</span>
          </div>
        </section>

        <!-- Row One -->
        <div class="row-one">
          <iq-card class="snapshot-card">
            <div class="snapshot-header">
              <div>
                <h4>Today's Cruise Snapshot</h4>
                <p class="text-muted">Summary of vessel position & speed performance over the last 24 hours.</p>
              </div>
              <span class="tag tag-outline">24H Window</span>
            </div>
            <div class="snapshot-image">
              <img :src="currAsset.massImage !== '' ? '/api/' + currAsset.massImage : ''" :alt="currAsset.massImage">
              <div class="snapshot-overlay">
                <div>
                  <h5 class="text-white">{{ currAsset.massName }}</h5>
                  <div class="snapshot-meta">
                    <span>Device Type: {{ currAsset?.massSystemType ?? '-' }}</span>
                    <span>IMO / ID: {{ currAsset?.massId ?? '-' }}</span>
                    <!-- <span>Fuel: HSD</span> -->
                  </div>
                </div>
                <button class="detail-button" @click="openAssetDetailModal(currAsset)">Detail</button>
              </div>
            </div>
            <div class="snapshot-stats">
              <div class="snapshot-stat">
                <div class="stat-header">
                  <span>Current Coordinate</span>
                  <span class="pill pill-blue">GPS</span>
                </div>
                <h5>{{ convertDecimalToDMS(currAssetLastData?.gps?.latitude, true) }}</h5>
                <p>{{ convertDecimalToDMS(currAssetLastData?.gps?.longitude, false) }}</p>
              </div>
              <div class="snapshot-stat">
                <div class="stat-header">
                  <span>Current Speed</span>
                  <span class="pill pill-pink">Knot</span>
                </div>
                <h5>{{ currAssetLastData?.gps?.speed ? (currAssetLastData?.gps?.speed * 0.5399568)?.toFixed(2) : '-' }}</h5>
                <p>{{ currAssetLastData?.gps?.timestamp }}</p>
              </div>
              <div class="snapshot-stat">
                <div class="stat-header">
                  <span>Total Cruise Today</span>
                  <span class="pill pill-blue">NM</span>
                </div>
                <h5>{{ currAssetSummary.gps.totalDistance }}</h5>
                <p>24H Duration</p>
              </div>
              <div class="snapshot-stat">
                <div class="stat-header">
                  <span>Average Speed</span>
                  <span class="pill pill-pink">Knot</span>
                </div>
                <h5>{{ currAssetSummary.gps.averageSpeed }}</h5>
                <p>24H Duration</p>
              </div>
            </div>
          </iq-card>

          <iq-card class="fuel-card">
            <div class="snapshot-header">
              <div>
                <h4>Fuel Consumption Today</h4>
                <p class="text-muted">Main engine fuel consumption trend over the last 24 hours.</p>
              </div>
              <span class="tag tag-outline">24H Window</span>
            </div>
            <div class="fuel-section">
              <div class="fuel-header">
                <div>
                  <h5>Port Fuel Cons</h5>
                  <span>Avg Flow: <strong>{{ currAssetSummary.fm.port.avgFuelFlow }}</strong> L/h</span>
                </div>
                <div class="fuel-value">
                  <strong>{{ currAssetSummary.fm.port.engineCons }}</strong>
                  <span>Liter</span>
                </div>
              </div>
              <port-flow-rate-chart v-if="currAssetSummary.series && currAssetSummary.series.length > 0"
                :series="currAssetSummary.series" />
            </div>
            <div class="fuel-section">
              <div class="fuel-header">
                <div>
                  <h5>Starboard Fuel Cons</h5>
                  <span>Avg Flow: <strong>{{ currAssetSummary.fm.starboard.avgFuelFlow }}</strong> L/h</span>
                </div>
                <div class="fuel-value">
                  <strong>{{ currAssetSummary.fm.starboard.engineCons }}</strong>
                  <span>Liter</span>
                </div>
              </div>
              <starboard-flow-rate-chart v-if="currAssetSummary.series && currAssetSummary.series.length > 0"
                :series="currAssetSummary.series" />
            </div>
            <div class="fuel-summary">
              <div>
                <h5>Total ME Cons</h5>
                <p>{{ currAssetSummary.fm.meEngineCons }} <span>Liter</span></p>
                <span>Avg ME Flow: {{ avgCombinedFlow }} L/h</span>
              </div>
              <div>
                <h5>Total AE Cons</h5>
                <p>{{ totalAeCons }} <span>Liter</span></p>
                <!-- <span>Avg AE Flow: - L/h</span> -->
              </div>
            </div>
            <div class="fuel-total">
              <div>
                <h5>Total Engine Consumption</h5>
                <span>Avg Combined Flow: {{ avgCombinedFlow }} L/h</span>
              </div>
              <div class="fuel-value">
                <strong>{{ totalEngineCons }}</strong>
                <span>Liter</span>
              </div>
            </div>
          </iq-card>
        </div>
        <!-- ./ Row one -->

        <section class="engine-section">
          <div class="iq-card section-card">
            <div class="section-header">
              <div>
                <h4>Current Engine Data - Main Engine</h4>
                <p class="text-muted">Monitoring realtime data sensor MANTRAZ.</p>
              </div>
              <span class="tag tag-outline">Live Telemetry</span>
            </div>
            <div class="current-data-row">
              <div class="iq-card">
              <div class="engine-column-header">
                <h5 >PORT</h5>
              </div>
              <div class="iq-card-body pt-2">
                <div class="stat-card-grid">
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">Current RPM</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.rpm?.port?.toFixed(0) ?? '-' }}</span>
                        <span class="stat-unit">RPM</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.rpm?.timestampPort ?? '-' }}</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">FM Flowrate</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.fm?.port?.flowrate ?? '-' }}</span>
                        <span class="stat-unit">L/h</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.fm?.port?.timestamp ?? '-' }}</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">FM Density</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.fm?.port?.density?.toFixed(2) ?? '-' }}</span>
                        <span class="stat-unit">g/cm³</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.fm?.port?.timestamp ?? '-' }}</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">FM Temp.</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.fm?.port?.temp ?? '-' }}</span>
                        <span class="stat-unit">°C</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.fm?.port?.timestamp ?? '-' }}</span>
                    </div>
                  </div>
                  <div class="stat-card" v-if='currAsset?.devices?.some(d => d?.devcLabel?.toLowerCase() === "exhaust_temperature")'>
                    <div class="stat-text-content">
                      <span class="stat-title">Exhaust Temp.</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.temp?.port?.exhaust ?? '-' }}</span>
                        <span class="stat-unit">°C</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.temp?.port?.timestampExhaust ?? '-' }}</span>
                    </div>
                  </div>
                  <div class="stat-card" v-if='currAsset?.devices?.some(d => d?.devcLabel?.toLowerCase() === "temperature")'>
                    <div class="stat-text-content">
                      <span class="stat-title">Reservoir Temp.</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.temp?.port?.reservoir ?? '-' }}</span>
                        <span class="stat-unit">°C</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.temp?.port?.timestampReservoir }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="iq-card">
              <div class="engine-column-header">
                <h5>STARBOARD</h5>
              </div>
              <div class="iq-card-body pt-2">
                <div class="stat-card-grid">
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">Current RPM</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.rpm?.starboard?.toFixed(0) ?? '-' }}</span>
                        <span class="stat-unit">rpm</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.rpm?.timestampPort }}</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">FM Flowrate</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.fm?.starboard?.flowrate ?? '-' }}</span>
                        <span class="stat-unit">L/h</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.fm?.port?.timestamp }}</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">FM Density</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.fm?.starboard?.density?.toFixed(2) ?? '-' }}</span>
                        <span class="stat-unit">g/cm³</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.fm?.port?.timestamp }}</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-text-content">
                      <span class="stat-title">FM Temp.</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.fm?.starboard?.temp ?? '-' }}</span>
                        <span class="stat-unit">°C</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.fm?.port?.timestamp }}</span>
                    </div>
                  </div>
                  <div class="stat-card" v-if='currAsset?.devices?.some(d => d?.devcLabel?.toLowerCase() === "exhaust_temperature")'>
                    <div class="stat-text-content">
                      <span class="stat-title">Exhaust Temp.</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.temp?.starboard?.exhaust ?? '-' }}</span>
                        <span class="stat-unit">°C</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.temp?.port?.timestampExhaust }}</span>
                    </div>
                  </div>
                  <div class="stat-card" v-if='currAsset?.devices?.some(d => d?.devcLabel?.toLowerCase() === "temperature")'>
                    <div class="stat-text-content">
                      <span class="stat-title">Reservoir Temp.</span>
                      <div class="stat-reading">
                        <span class="stat-value">{{ currAssetLastData?.temp?.starboard?.reservoir ?? '-' }}</span>
                        <span class="stat-unit">°C</span>
                      </div>
                      <span class="stat-date">{{ currAssetLastData?.temp?.port?.timestampReservoir }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </section>

        <section class="summary-section">
          <div class="iq-card section-card">
            <div class="section-header">
              <div>
                <h4>Main Engine & Auxiliary Engine Summary</h4>
                <p class="text-muted">24-hour summary of main engine run hours, average RPM, exhaust temperature, and auxiliary engine.</p>
              </div>
              <span class="tag tag-outline">24H Window</span>
            </div>
            <div class="summary-grid">
              <div class="iq-card">
              <div class="iq-card-header d-flex justify-content-between pt-2">
                <div class="iq-header-title">
                  <h5 class="card-title">PORT MAIN ENGINE</h5>
                  <span class="text-muted m-sm-0">Last Updated {{ currAssetSummary.rpm.port.lastData }}</span>
                </div>
              </div>
              <div class="iq-card-body pt-0">
                <div class="row mt-2">
                  <div class="col-7 p-0 rpm-chart-wrapper">
                    <apexchart element="chart-14" :options="rpmChart"
                      :series="[((currAssetLastData?.rpm?.port?.toFixed(0) / 1600) * 100).toFixed(2)]" />
                  </div>
                  <b-col md="5" sm="12" class="chart-info-wrapper">
                    <div>
                      <p class="chart-title">Engine Hour</p>
                      <p class="chart-info"><b>{{ currAssetSummary.rpm.port.runningTime }}</b> Hour</p>
                    </div>
                    <div>
                      <p class="chart-title">Average RPM</p>
                      <p class="chart-info"><b>{{ currAssetSummary.rpm.port.average }}</b></p>
                    </div>
                      <div v-if='currAsset?.devices?.some(d => d?.devcLabel?.toLowerCase() === "exhaust_temperature")'>
                        <p class="chart-title">Exhaust Temp</p>
                        <p class="chart-info"><b>{{ currAssetLastData?.temp?.port?.exhaust ?? '-' }}</b> °C</p>
                      </div>
                  </b-col>
                </div>
              </div>
            </div>

            <div class="iq-card">
              <div class="iq-card-header d-flex justify-content-between pt-2">
                <div class="iq-header-title">
                  <h5 class="card-title">STARBOARD MAIN ENGINE</h5>
                  <span class="text-muted m-sm-0">Last Updated {{ currAssetSummary.rpm.starboard.lastData }}</span>
                </div>
              </div>
              <div class="iq-card-body pt-0">
                <div class="row mt-2">
                  <div class="col-7 p-0 rpm-chart-wrapper">
                    <apexchart element="chart-14" :options="rpmChart"
                      :series="[((currAssetLastData?.rpm?.starboard?.toFixed(0) / 1600) * 100).toFixed(2)]" />
                  </div>
                  <b-col md="5" sm="12" class="chart-info-wrapper">
                    <div>
                      <p class="chart-title">Engine Hour</p>
                      <p class="chart-info"><b>{{ currAssetSummary.rpm.starboard.runningTime }}</b> Hour</p>
                    </div>
                    <div>
                      <p class="chart-title">Average RPM</p>
                      <p class="chart-info"><b>{{ currAssetSummary.rpm.starboard.average }}</b></p>
                    </div>
                      <div v-if='currAsset?.devices?.some(d => d?.devcLabel?.toLowerCase() === "exhaust_temperature")'>
                        <p class="chart-title">Exhaust Temp</p>
                        <p class="chart-info"><b>{{ currAssetLastData?.temp?.starboard?.exhaust ?? '-' }}</b> °C</p>
                      </div>
                  </b-col>
                </div>
              </div>
            </div>

            <div class="ae-column">
              <div class="iq-card ae-card">
                <div class="iq-card-body">
                  <div class="d-flex justify-content-between">
                    <h4 class="ae-title">Auxiliary Engine 1</h4>
                    <span class="badge badge-success" v-if="currAssetSummary.ae.ae1.lastRunningTime > 0">ON</span>
                    <span class="badge badge-danger" v-else>OFF</span>
                  </div>
                  <p class="ae-hour">{{ currAssetSummary.ae.ae1.runningSeconds }} <span
                      class="text-muted ae-unit">hour</span></p>
                  <span class="ae-cons">AE Cons: {{ typeof currAssetSummary?.ae?.ae1?.fuelConsumption === 'number' ? currAssetSummary.ae.ae1.fuelConsumption.toFixed(2) : '-' }} L</span>
                </div>
              </div>
              <div class="iq-card ae-card">
                <div class="iq-card-body">
                  <div class="d-flex justify-content-between">
                    <h4 class="ae-title">Auxiliary Engine 2</h4>
                    <span class="badge badge-success" v-if="currAssetSummary.ae.ae2.lastRunningTime > 0">ON</span>
                    <span class="badge badge-danger" v-else>OFF</span>
                  </div>
                  <p class="ae-hour">{{ currAssetSummary.ae.ae2.runningSeconds }} <span
                      class="text-muted ae-unit">hour</span></p>
                  <span class="ae-cons">AE Cons: {{ typeof currAssetSummary?.ae?.ae2?.fuelConsumption === 'number' ? currAssetSummary.ae.ae2.fuelConsumption.toFixed(2) : '-' }} L</span>
                </div>
              </div>
              <div class="iq-card ae-card">
                <div class="iq-card-body">
                  <div class="d-flex justify-content-between">
                    <h4 class="ae-title">Auxiliary Engine 3</h4>
                    <span class="badge badge-success" v-if="currAssetSummary.ae.ae3.lastRunningTime > 0">ON</span>
                    <span class="badge badge-danger" v-else>OFF</span>
                  </div>
                  <p class="ae-hour">{{ currAssetSummary.ae.ae3.runningSeconds }} <span
                      class="text-muted ae-unit">hour</span></p>
                  <span class="ae-cons">AE Cons: {{ typeof currAssetSummary?.ae?.ae3?.fuelConsumption === 'number' ? currAssetSummary.ae.ae3.fuelConsumption.toFixed(2) : '-' }} L</span>
                </div>
              </div>
            </div>
            </div>
          </div>
        </section>

        <div class="row-four">
          <div class="map-section">
            <Map
              v-if="currAsset"
              :ships="[currAsset]"
              SelectedMap="default"
              :center="center"
              :zoom="zoom"
              style="border-radius: 20px; margin-top: 10px; max-height: 100%;"
              :Options="{ enableTrail: true }"
            />
          </div>
        </div>

        <div class="row-five">
          <div class="iq-card">
            <div class="iq-card-header d-flex justify-content-between">
              <div class="iq-header-title">
                <h5 class="card-title">RPM, Fuel Consumption, And Speed Chart</h5>
              </div>
            </div>
            <div class="card-info-container">
              <div class="card-info-section">
                <h5 class="">{{ currAssetSummary.rpm.port.average }}</h5>
                <span class="">Avg. Port RPM</span>
              </div>
              <div class="card-info-section">
                <h5 class="">{{ currAssetSummary.fm.port.engineCons }} Liter</h5>
                <span class="">Port Fuel Cons</span>
              </div>
              <div class="card-info-section">
                <h5 class="">{{ currAssetSummary.rpm.starboard.average }}</h5>
                <span class="">Avg. Stbd. RPM</span>
              </div>
              <div class="card-info-section">
                <h5 class="">{{ currAssetSummary.fm.starboard.engineCons }} Liter</h5>
                <span class="">Stbd. Fuel Cons</span>
              </div>
              <div class="card-info-section">
                <h5 class="">{{ currAssetSummary.gps.averageSpeed }} Knot</h5>
                <span class="">Avg. Speed</span>
              </div>
            </div>

            <rpm-fuel-speed class="mx-3 mb-3" :series="currAssetSummary.series" />
          </div>
        </div>
      </template>

      <template v-else-if="!currAssetSummary">
        <div class="text-center mt-5">
          <h5>No Data</h5>
        </div>
      </template>
    </div>

    <!-- Asset Detail Modal (same as /master/asset) -->
    <b-modal
      size="lg"
      title="Asset Detail"
      hide-footer
      v-model="displayAssetDetail"
      body-class="p-0"
    >
      <div v-if="detailAsset && detailAsset.massId">
        <div class="bg-light p-3 d-flex align-items-start border-bottom">
          <img
            :src="detailAsset.massImage !== '' ? '/api/' + detailAsset.massImage : 'https://placehold.co/120x120?text=No+Image'"
            class="rounded mr-3 shadow-sm"
            style="width: 100px; height: 100px; object-fit: cover;"
          />
          <div class="flex-grow-1">
            <h5 class="font-weight-bold mb-1">{{ detailAsset.massName }}</h5>
            <div class="mb-2">
              <b-badge variant="primary" class="mr-1">{{ detailAsset.massSystemType }}</b-badge>
              <b-badge variant="info">{{ detailAsset.massMaty ? detailAsset.massMaty.matyName : 'Unknown Type' }}</b-badge>
            </div>
            <div class="text-muted small">
              <i class="ri-calendar-line mr-1"></i> Installation Date:
              <span class="text-dark font-weight-bold">{{ detailAsset.massInstallationDate || '-' }}</span>
            </div>
          </div>
        </div>

        <div class="p-3">
          <b-row>
            <b-col md="6" class="border-right border-md-0">
              <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold border-bottom pb-1">
                <i class="ri-user-star-line mr-1"></i> Contact Person (PIC)
              </h6>
              <div class="detail-row mb-2">
                <span class="text-muted small">Name</span>
                <span class="font-weight-bold d-block">{{ detailAsset.massPicName || '-' }}</span>
              </div>
              <div class="detail-row mb-2">
                <span class="text-muted small">Contact / Phone</span>
                <span class="font-weight-bold d-block">{{ detailAsset.massPicContact || '-' }}</span>
              </div>
              <div class="detail-row mb-2">
                <span class="text-muted small">Email</span>
                <span class="font-weight-bold d-block">{{ detailAsset.massPicEmail || '-' }}</span>
              </div>
              <div class="detail-row mb-2">
                <span class="text-muted small">Email CC</span>
                <span class="font-weight-bold d-block">{{ detailAsset.massPicEmailCc || '-' }}</span>
              </div>
            </b-col>

            <b-col md="6">
              <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold border-bottom pb-1">
                <i class="ri-wifi-line mr-1"></i> Connectivity & Hardware
              </h6>
              <b-row>
                <b-col cols="6" class="mb-2">
                  <span class="text-muted small d-block">GSM Number</span>
                  <span class="font-weight-bold">{{ detailAsset.massGsmNo || '-' }}</span>
                </b-col>
                <b-col cols="6" class="mb-2">
                  <span class="text-muted small d-block">IMEI</span>
                  <span class="font-weight-bold">{{ detailAsset.massImei || '-' }}</span>
                </b-col>
                <b-col cols="6" class="mb-2">
                  <span class="text-muted small d-block">Telco Operator</span>
                  <span class="font-weight-bold">{{ detailAsset.massTelcoOperator || '-' }}</span>
                </b-col>
                <b-col cols="6" class="mb-2">
                  <span class="text-muted small d-block">Satellite ID</span>
                  <span class="font-weight-bold">{{ detailAsset.massSateliteId || '-' }}</span>
                </b-col>
                <b-col cols="6" class="mb-2">
                  <span class="text-muted small d-block">Sat. Operator</span>
                  <span class="font-weight-bold">{{ detailAsset.massSateliteOperator || '-' }}</span>
                </b-col>
                <b-col cols="6" class="mb-2">
                  <span class="text-muted small d-block">Sat. Device ID</span>
                  <span class="font-weight-bold">{{ detailAsset.massSatDeviceId || '-' }}</span>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>

        <div class="p-3 border-top bg-white">
          <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold">
            <i class="ri-sensor-line mr-1"></i> Connected Devices
          </h6>
          <div v-if="detailDevices && detailDevices.length > 0" class="table-responsive">
            <table class="table table-sm table-borderless table-striped small mb-0">
              <thead class="bg-light">
                <tr>
                  <th class="pl-3">Name</th>
                  <th>Type</th>
                  <th>Unique ID</th>
                  <th>Status</th>
                  <th class="text-right pr-3">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dev in detailDevices" :key="dev.devcId">
                  <td class="font-weight-bold pl-3">{{ dev.devcName }}</td>
                  <td>
                    <b-badge variant="soft-info" class="text-uppercase">{{ dev.devcType }}</b-badge>
                  </td>
                  <td class="text-muted font-italic">{{ dev.devcUniqueId }}</td>
                  <td>
                    <b-badge :variant="dev.devcStatus === 1 ? 'success' : 'secondary'">
                      {{ dev.devcStatus === 1 ? 'Active' : 'Inactive' }}
                    </b-badge>
                  </td>
                  <td class="text-right pr-3">
                    <b-button
                      variant="light"
                      size="sm"
                      class="rounded-circle text-primary p-0 d-flex align-items-center justify-content-center border-0"
                      style="width: 24px; height: 24px;"
                      @click="openDeviceDetail(dev)"
                      v-b-tooltip.hover
                      title="View Device Data"
                    >
                      <i class="ri-eye-line"></i>
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-muted small text-center py-2 bg-light rounded">
            No devices linked to this asset.
          </div>
        </div>

        <div class="bg-lighter p-2 px-3 border-top d-flex justify-content-between small text-muted">
          <div>
            Created: <b>{{ detailAsset.massCreatedUserId }}</b> ({{ detailAsset.massCreatedTime }})
          </div>
          <div>
            Updated: <b>{{ detailAsset.massUpdatedUserId }}</b> ({{ detailAsset.massUpdatedTime }})
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import Map from '@/views/map';
import DataFilter from '@/components/DataFilter';
import PortFlowRateChart from '@/components/charts/PortFlowRateChart';
import StarboardFlowRateChart from '@/components/charts/StarboardFlowRateChart';
import RpmFuelSpeed from '@/components/charts/RpmFuelSpeed';
import { buildFmsSummary } from '@/util/fms-summary';
import DeviceSvc from '@/services/DeviceSvc';

import cruiseDistanceIcon from '@/assets/images/dashboard/cruise-distance.svg';
import cruiseLocationIcon from '@/assets/images/dashboard/cruise-location.svg';
import cruiseSpeedIcon from '@/assets/images/dashboard/cruise-speed.svg';

export default {
  name: 'FmsDashboard',
  components: {
    Map,
    DataFilter,
    // DataPercentage,
    PortFlowRateChart,
    StarboardFlowRateChart,
    RpmFuelSpeed,
  },
  data() {
    const rpmChart = {
      chart: {
        width: '100%',
        height: '100%',
        type: 'radialBar',
      },
      plotOptions: {
        hollow: {
          margin: 5,
          size: '50%',
          background: 'transparent',
          image: undefined,
          imageWidth: 64,
          imageHeight: 64,
        },
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          dataLabels: {
            name: {
              fontSize: '14px',
              color: undefined,
              offsetY: 110,
            },
            value: {
              offsetY: 70,
              fontSize: '19px',
              color: undefined,
              formatter(val) {
                return ((val / 100) * 1600).toFixed(2);
              },
            },
          },
        },
      },
      fill: {},
      stroke: {
        dashArray: 4,
      },
      labels: ['Current RPM'],
      colors: ['#0084ff'],
    };

    return {
      // eslint-disable-next-line no-undef
      center: L.LatLng(0, 0),
      zoom: 10,
      img: {
        cruiseDistanceIcon,
        cruiseLocationIcon,
        cruiseSpeedIcon,
      },
      masks: {
        input: 'DD-MM-YYYY HH:mm',
      },
      rpmChart,
      isLoading: true,
      displayAssetDetail: false,
      detailAsset: {},
      detailDevices: [],
    };
  },
  async mounted() {
    const interval = this.$store.getters['filter/interval'];
    if (interval === 'minute') {
      this.$store.commit('filter/SET_INTERVAL', 'hour');
    }
    const date = this.getLast24HoursRange();
    this.$store.commit('filter/SET_DATE', date);

    let currentMassId = this.$store.getters['filter/currentMassId'];
    let assets = this.$store.getters['asset/assets'];

    if (assets.length === 0) {
      await this.$store.dispatch('asset/getAssetsData');
      await this.$store.dispatch('asset/getAssetsDevices');
      await this.$store.dispatch('asset/getLastData');
      assets = this.$store.getters['asset/assets'];
    }

    if (currentMassId === null) {
      await this.$store.dispatch('filter/setCurrentMassId', assets[0].massId);
      currentMassId = this.$store.getters['filter/currentMassId'];
    }

    console.log('[FMS] fetchSummary params:', {
      start: date.start,
      end: date.end,
      interval,
      massId: currentMassId,
    });
    await this.$store.dispatch('asset/fetchSummary', {
      start: date.start,
      end: date.end,
      interval,
      massId: currentMassId,
    });
    console.log('[FMS] summary series len:', this.currAssetSummary?.series?.length || 0);
    console.log('[FMS] summary series sample:', this.currAssetSummary?.series?.slice?.(0, 3));

    await this.$store.dispatch('asset/getLastData');
    setInterval(async () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      const diffTime = Math.abs(nextMidnight - now);
      if (diffTime < 1000) {
        await this.$store.dispatch('asset/getLastData');
      }
    }, 1000 * 60);

    this.isLoading = false;
  },
  computed: {
    currAsset() {
      const currMassId = this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
    currAssetLastData() {
      if (!this.currAsset) return null;

      const {
        lastGpsData,
        lastRpmData,
        lastFmData,
        lastTemperatureData,
      } = this.currAsset;

      const mappedData = {
        gps: {
          ...lastGpsData,
          timestamp: moment(lastGpsData.timestamp * 1000).format('DD MMMM YYYY HH:mm'),
        } || null,
        rpm: {
          port: null,
          starboard: null,
        },
        fm: {
          port: { flowrate: null, density: null, temp: null },
          starboard: { flowrate: null, density: null, temp: null },
        },
        temp: {
          port: { exhaust: null, reservoir: null },
          starboard: { exhaust: null, reservoir: null },
        },
      };

      const portRpm = (lastRpmData || []).find((d) => d.type === 'PORT');
      const starboardRpm = (lastRpmData || []).find((d) => d.type === 'STARBOARD');

      if (portRpm) {
        mappedData.rpm.port = portRpm.rpm;
        mappedData.rpm.timestampPort = moment(portRpm.timestamp * 1000).format('HH:mm:ss DD-MM-Y');
      }
      if (starboardRpm) {
        mappedData.rpm.starboard = starboardRpm.rpm;
        mappedData.rpm.timestampStarboard = moment(starboardRpm.timestamp * 1000).format('HH:mm:ss DD-MM-Y');
      }

      const portFm = (lastFmData || []).find((d) => d.device?.devcLabel === 'PORT_IN');
      const starboardFm = (lastFmData || []).find((d) => d.device?.devcLabel === 'STARBOARD_IN');

      if (portFm) {
        mappedData.fm.port.flowrate = portFm.volumeFlowrate;
        mappedData.fm.port.density = portFm.density;
        mappedData.fm.port.temp = portFm.temperature;
        mappedData.fm.port.timestamp = moment(portFm.timestamp * 1000).format('HH:mm:ss DD-MM-Y');
      }
      if (starboardFm) {
        mappedData.fm.starboard.flowrate = starboardFm.volumeFlowrate;
        mappedData.fm.starboard.density = starboardFm.density;
        mappedData.fm.starboard.temp = starboardFm.temperature;
        mappedData.fm.starboard.timestamp = moment(starboardFm.timestamp * 1000).format('HH:mm:ss DD-MM-Y');
      }

      (lastTemperatureData || []).forEach((data) => {
        const type = data.type;
        const label = data.device?.devcLabel;
        const tempValue = data.temperature;
        const timestamp = moment(data.timestamp * 1000).format('HH:mm:ss DD-MM-Y');

        if (type === 'PORT') {
          if (label === 'EXHAUST_TEMPERATURE') {
            mappedData.temp.port.exhaust = tempValue;
            mappedData.temp.port.timestampExhaust = timestamp;
          } else if (label === 'TEMPERATURE') {
            mappedData.temp.port.reservoir = tempValue;
            mappedData.temp.port.timestampReservoir = timestamp;
          }
        } else if (type === 'STARBOARD') {
          if (label === 'EXHAUST_TEMPERATURE') {
            mappedData.temp.starboard.exhaust = tempValue;
            mappedData.temp.starboard.timestampExhaust = timestamp;
          } else if (label === 'TEMPERATURE') {
            mappedData.temp.starboard.reservoir = tempValue;
            mappedData.temp.starboard.timestampReservoir = timestamp;
          }
        }
      });

      return mappedData;
    },
    currAssetSummary() {
      return buildFmsSummary({
        summary: this.currAsset?.summary,
        dateRange: this.dateRange,
        interval: this.interval,
        includeAeLastRunning: true,
        includeSeriesTemperature: true,
        seriesTemperatureKey: 'tempData',
      });
    },
    totalAeCons() {
      if (!this.currAssetSummary) return '-';
      const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);
      const aeValues = [
        toNumber(this.currAssetSummary.ae?.ae1?.fuelConsumption),
        toNumber(this.currAssetSummary.ae?.ae2?.fuelConsumption),
        toNumber(this.currAssetSummary.ae?.ae3?.fuelConsumption),
      ];
      const validValues = aeValues.filter((value) => value !== null);
      if (validValues.length === 0) return '-';
      const total = validValues.reduce((sum, value) => sum + value, 0);
      return total.toFixed(2);
    },
    totalEngineCons() {
      if (!this.currAssetSummary) return '-';
      const me = Number.isFinite(Number(this.currAssetSummary.fm?.meEngineCons))
        ? Number(this.currAssetSummary.fm.meEngineCons)
        : null;
      const ae = Number.isFinite(Number(this.totalAeCons)) ? Number(this.totalAeCons) : null;
      if (me === null && ae === null) return '-';
      const total = (me ?? 0) + (ae ?? 0);
      return total.toFixed(2);
    },
    avgCombinedFlow() {
      if (!this.currAssetSummary) return '-';
      const port = parseFloat(this.currAssetSummary.fm?.port?.avgFuelFlow || 0);
      const starboard = parseFloat(this.currAssetSummary.fm?.starboard?.avgFuelFlow || 0);
      return ((port + starboard) / 2).toFixed(2);
    },
    lastDataAgeMinutes() {
      const lastTimestamp = this.currAsset?.lastGpsData?.timestamp;
      if (!lastTimestamp) return null;
      return moment().diff(moment.unix(lastTimestamp), 'minutes', true);
    },
    isRealtimeConnected() {
      if (this.lastDataAgeMinutes === null) return false;
      return this.lastDataAgeMinutes <= 10;
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    rangeLabel() {
      const { start, end } = this.dateRange;
      const startLabel = moment(start).format('DD MMM YYYY HH:mm');
      const endLabel = moment(end).format('DD MMM YYYY HH:mm');
      return `${startLabel} - ${endLabel}`;
    },
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
  },
  methods: {
    ...mapActions(['updatekapalTrail']),
    zoomToShip(ship) {
      // eslint-disable-next-line no-undef
      this.center = L.LatLng(ship.position[0], ship.position[1]);
      this.zoom = 15;
    },
    async onApplyFilter() {
      this.isLoading = true;
      try {
        // await this.$store.dispatch('filter/setDate', this.dateRange);
        const date = this.getLast24HoursRange();
        this.$store.commit('filter/SET_DATE', date);
        await this.$store.dispatch('filter/setCurrentMassId', this.selectedAsset || this.currentMassId);
        await this.$store.dispatch('filter/setInterval', this.selectedInterval || this.interval);
        console.log('hehe');
        console.log('[FMS] fetchSummary params:', {
          start: date.start,
          end: date.end,
          interval: this.interval,
          massId: this.currentMassId,
        });
        await this.$store.dispatch('asset/fetchSummary', {
          start: date.start,
          end: date.end,
          interval: this.interval,
          massId: this.currentMassId,
        });
        console.log('huhu');
        console.log('[FMS] summary series len:', this.currAssetSummary?.series?.length || 0);
        console.log('[FMS] summary series sample:', this.currAssetSummary?.series?.slice?.(0, 3));
      } catch (err) {
        console.err('Apply Filter Error', err);
      } finally {
        this.isLoading = false;
      }
    },
    getLast24HoursRange() {
      const end = moment().startOf('hour');
      const start = end.clone().subtract(24, 'hours');
      return { start, end };
    },
    convertDecimalToDMS(decimal, isLatitude) {
      const degrees = Math.floor(Math.abs(decimal));
      const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
      const minutes = Math.floor(minutesDecimal);
      const seconds = Math.round((minutesDecimal - minutes) * 60);

      // eslint-disable-next-line no-nested-ternary
      const direction = decimal >= 0
        ? (isLatitude ? 'N' : 'E')
        : (isLatitude ? 'S' : 'W');

      return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
    },
    async openAssetDetailModal(asset) {
      if (!asset) return;
      this.detailAsset = asset;
      this.detailDevices = [];
      try {
        const param = {
          findField: 'devcMassId',
          findValue: asset.massId,
          max: 100,
        };
        const devices = await DeviceSvc.getDevicesByAssetId(param);
        if (devices && devices.data && devices.data.data) {
          this.detailDevices = devices.data.data;
        }
      } catch (e) {
        console.error('Failed to fetch devices', e);
      }
      this.displayAssetDetail = true;
    },
    openDeviceDetail(data) {
      this.$router.push({ name: 'DeviceDetail', params: { id: data.devcId } });
    },
  },
};
</script>

<style scoped>
.fms-dashboard {
  width: 100%;
  margin: 0 auto;
  background: #f5f7fb;
  padding-bottom: 30px;
}

.dashboard-card {
  margin: 10px;
  padding: 10px 0 20px;
  background: transparent;
}

.dashboard-card .iq-card {
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  box-shadow: none;
}

.fms-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px 0;
  flex-wrap: wrap;
  gap: 15px;
}

.fms-header h1 {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #1d2433;
}

.overview-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tag {
  background: #e9efff;
  color: #2f5cff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.tag-soft {
  background: #f1f4f9;
  color: #4c5c77;
}

.tag-outline {
  background: #f1f4ff;
  border: 1px solid #cdd8ff;
}

.header-status {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e9f9ef;
  color: #2a9d55;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 600;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2a9d55;
}

.status-pill--alert {
  background: #fee2e2;
  color: #b91c1c;
}

.status-dot--alert {
  background: #ef4444;
}

.status-dot-green {
  background: #29c56a;
}

.row-one {
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  grid-gap: 16px;
  padding: 0 10px;
  margin-top: 10px;
}

.snapshot-card {
  padding: 15px 20px;
}

.snapshot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.snapshot-header h4 {
  margin: 0;
  font-weight: 700;
}

.snapshot-image {
  position: relative;
  margin-top: 15px;
  border-radius: 16px;
  overflow: hidden;
  height: 220px;
}

.snapshot-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.snapshot-overlay {
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(20, 24, 36, 0.6);
  color: #fff;
  padding: 12px 16px;
  border-radius: 12px;
  backdrop-filter: blur(6px);
}

.snapshot-overlay h5 {
  margin: 0 0 4px;
  font-size: 16px;
}

.snapshot-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 11px;
}

.detail-button {
  border: none;
  background: #ffffff;
  color: #1d2433;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
}

.snapshot-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.snapshot-stat {
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  padding: 12px 14px;
}

.snapshot-stat h5 {
  margin: 8px 0 4px;
  font-size: 16px;
}

.snapshot-stat p {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
}

.pill {
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 10px;
}

.pill-blue {
  background: #dbeafe;
  color: #2563eb;
}

.pill-pink {
  background: #fce7f3;
  color: #db2777;
}

.fuel-card {
  padding: 15px 20px;
}

.fuel-section {
  margin-top: 12px;
  padding: 12px;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
}

.fuel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.fuel-header h5 {
  margin: 0 0 4px;
  font-size: 14px;
}

.fuel-header span {
  font-size: 12px;
  color: #6b7280;
}

.fuel-value {
  text-align: right;
}

.fuel-value strong {
  font-size: 16px;
  color: #1d2433;
}

.fuel-value span {
  font-size: 11px;
  color: #6b7280;
}

.fuel-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
}

.fuel-summary div {
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  padding: 10px 12px;
}

.fuel-summary h5 {
  margin: 0 0 6px;
  font-size: 13px;
  color: #1d2433;
}

.fuel-summary p {
  margin: 0 0 4px;
  font-size: 14px;
}

.fuel-total {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #edf1f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.engine-section,
.summary-section {
  padding: 0 10px;
  margin-top: 14px;
}

.summary-section .section-card {
  padding: 10px 10px 14px;
}

.summary-section .summary-grid {
  padding: 0 10px 10px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  gap: 10px;
}

.section-header h4 {
  margin: 0 0 4px;
  font-weight: 700;
}

.engine-column-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 15px 15px 0;
}

.engine-column-header h6 {
  margin: 0;
  font-size: 12px;
  letter-spacing: 1px;
}

.engine-update {
  font-size: 11px;
  color: #6b7280;
  margin-top: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 0.8fr;
  gap: 12px;
}

.ae-column {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.ae-card {
  padding: 12px;
}

.ae-cons {
  font-size: 14px;
  color: #6b7280;
}

.stat-card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

.stat-card {
  display: flex;
  flex-direction: row;
  /* Diubah menjadi row */
  align-items: center;
  /* Rata tengah vertikal */
  justify-content: center;
  /* Rata tengah horizontal */
  background-color: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  padding: 12px 14px;
  box-shadow: none;
}

/* Style baru untuk konten teks */
.stat-text-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Diubah menjadi center (semula flex-start) */
}

.stat-title {
  font-size: 0.85rem;
  /* Judul lebih kecil */
  font-weight: 500;
  color: #555;
  margin-bottom: 4px;
  /* Jarak antara judul dan data */
}

.stat-reading {
  display: flex;
  align-items: baseline;
  gap: 4px;
  /* Jarak antara angka dan satuan */
}

.stat-value {
  font-size: 1.15rem;
  /* Nilai sedikit disesuaikan */
  font-weight: 700;
  color: #111;
}

.stat-unit {
  font-size: 0.8rem;
  color: #666;
  font-weight: 500;
}

.stat-date {
  font-size: 0.7rem;
  color: #666;
  font-weight: 500;
}

.iq-card-header-toolbar p {
  margin: 0;
}

@media screen and (max-width: 991px) {
  .fms-header {
    padding: 10px 10px 0;
  }

  .row-one {
    grid-template-columns: 1fr;
  }

  .snapshot-stats {
    grid-template-columns: 1fr 1fr;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .fuel-summary {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 575px) {
  .snapshot-stats {
    grid-template-columns: 1fr;
  }
}

.ship-info .ship-title {
  font-size: 1rem;
  font-weight: 500;
}

.ship-info .ship-subtitle {
  font-size: 1rem;
  font-weight: 700;
}

.ship-info .ship-subtitle span {
  font-size: 12px;
  font-weight: 700;
}

.chart-title {
  font-size: 16px;
  color: #2c3e50;
  margin: 0;
}

.chart-info {
  font-size: 25px;
  color: #0084ff;
  margin: 0;
}

.chart-info-green {
  font-size: 25px;
  color: #55c95a;
  margin: 0;
}

.current-data-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 5px;
  padding: 0 10px;
  padding-bottom: 15px;
}

.ae-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0;
}

.ae-hour {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 4px 0;
  line-height: 24px;
}

.ae-unit {
  font-size: 12px;
}

.row-four {
  display: grid;
  grid-template-columns: 1fr;
  min-height: 454px;
  grid-gap: 10px;
  padding: 0 10px;
  margin-top: 14px;
}

.map-section {
  min-height: 454px;
}

.row-five {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 5px;
  padding: 5px 5px;
  margin-top: 20px;
}

.card-info-container {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  padding: 10px 15px;
}

.card-info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

@media screen and (max-width: 991px) {
  .current-data-row {
    grid-template-columns: 1fr;
  }

  .stat-card-grid {
    grid-template-columns: 1fr 1fr;
  }

  .stat-card {
    flex-direction: column;
    padding: 12px 10px;
  }

  .chart-title {
    font-size: 12px;
  }

  .chart-info-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 5px;
    padding-left: 20px;
  }

  .rpm-chart-wrapper {
    display: none;
  }

  .row-four {
    min-height: 454px;
    grid-gap: 10px;
    padding: 0 10px;
  }

  .map-section {
    min-height: 454px;
  }

  .row-five {
    grid-template-columns: 1fr;
    grid-gap: 5px;
    padding: 5px 5px;
    margin-top: 20px;
  }

  .card-info-container {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows: auto auto;
  }
}
</style>
